import { getPageImage } from "@/lib/page-imagery";
import { LangLink } from "@/components/LangLink";
import { useLanguage } from "@/lib/language";
import type { InsightMeta } from "@/components/insights/InsightShell";
import { H2, P, TableWrap, thClass, tdClass } from "@/components/insights/prose";

export const accreditedVsNonMeta: InsightMeta = {
  slug: "accredited-vs-non-accredited-iso-certificate",
  metaTitle: {
    en: "Accredited vs Non-Accredited ISO Certificate | IraqCert",
    ar: "الشهادة المعتمدة مقابل غير المعتمدة | عراق سيرت",
  },
  title: {
    en: "Accredited vs Non-Accredited ISO Certification: The Difference",
    ar: "الشهادة المعتمدة مقابل غير المعتمدة: ما الفرق؟",
  },
  description: {
    en: "Understand accredited and non-accredited ISO certification, check buyer requirements, and verify certificate status and scope.",
    ar: "تعرّف على الفرق بين شهادات الأيزو المعتمدة وغير المعتمدة، وكيف تراجع متطلبات المشتري وتتحقق من حالة الشهادة ونطاقها.",
  },
  datePublished: "2026-07-21",
  dateModified: "2026-09-29",
  dateLabel: { en: "29 September 2026", ar: "29 أيلول 2026" },
  enquiryTitle: {
    en: "Discuss an accredited certification requirement",
    ar: "ناقش متطلب شهادة أيزو معتمدة",
  },
  enquiryButton: {
    en: "Discuss your certification requirement",
    ar: "ناقش متطلب الشهادة",
  },
  enquiryMessage: {
    en: "Hello IraqCert, I would like help with an accredited ISO certification requirement.\nRequested standard:\nBusiness activity and sites:\nCustomer requirement:\nPage: /insights/accredited-vs-non-accredited-iso-certificate",
    ar: "مرحباً IraqCert، أرغب في الحصول على مساعدة بشأن متطلب شهادة أيزو معتمدة.\nالمواصفة المطلوبة:\nنشاط الشركة ومواقعها:\nمتطلب العميل:\nالصفحة: /ar/insights/accredited-vs-non-accredited-iso-certificate",
  },
  image: getPageImage("/insights/accredited-vs-non-accredited-iso-certificate", "editorial"),
  keyPoints: [
    {
      en: "Name the certification body and check the accreditation scope for the proposed certificate.",
      ar: "حدّد جهة منح الشهادة وتحقّق من نطاق اعتمادها بالنسبة إلى الشهادة المقترحة.",
    },
    {
      en: "Match the standard, activities and sites to the customer’s written requirement.",
      ar: "طابق المواصفة والأنشطة والمواقع مع متطلب العميل المكتوب.",
    },
    {
      en: "A missing database result needs follow-up with the relevant body.",
      ar: "إذا لم يظهر السجل في القاعدة، فاستكمل التحقق مع الجهة المعنية.",
    },
  ],
  sections: [
    {
      id: "meaning",
      label: {
        en: "What accreditation tells you",
        ar: "ماذا يثبت الاعتماد؟",
      },
    },
    {
      id: "buyer",
      label: {
        en: "Match the buyer’s requirement",
        ar: "مطابقة متطلب المشتري",
      },
    },
    {
      id: "verify",
      label: {
        en: "Verify the certificate",
        ar: "التحقق من الشهادة",
      },
    },
    {
      id: "iraqcert-role",
      label: {
        en: "IraqCert’s role",
        ar: "دور IraqCert",
      },
    },
  ],
  faq: [
    {
      q: { en: "Can anyone issue an ISO certificate?", ar: "هل يمكن لأي جهة إصدار شهادة أيزو؟" },
      a: {
        en: "ISO develops standards; independent certification bodies audit organizations and issue certificates. Accreditation provides independent confirmation of a certification body’s competence within a defined scope.",
        ar: "تضع منظمة ISO المواصفات، بينما تتولى جهات منح شهادات مستقلة تدقيق المؤسسات وإصدار الشهادات. ويوفر الاعتماد تأكيداً مستقلاً لكفاءة الجهة المانحة ضمن نطاق محدد.",
      },
    },
    {
      q: {
        en: "Is a non-accredited certificate ever useful?",
        ar: "هل الشهادة غير المعتمدة مفيدة أحياناً؟",
      },
      a: {
        en: "Acceptance depends on the buyer’s requirements. A non-accredited certificate does not meet an explicit requirement for accredited certification. ISO notes that non-accreditation alone does not mean a certification body is not reputable.",
        ar: "يتوقف القبول على متطلبات المشتري. فالشهادة غير المعتمدة لا تلبّي شرطاً صريحاً بالحصول على شهادة معتمدة. وتوضح منظمة ISO أن غياب الاعتماد وحده لا يعني أن الجهة المانحة غير موثوقة.",
      },
    },
    {
      q: {
        en: "Does accreditation guarantee buyer acceptance?",
        ar: "هل يضمن الاعتماد قبول الشهادة لدى المشتري؟",
      },
      a: {
        en: "No. Check the required standard, certified activities and sites, current validity, and any named certification or accreditation body. The buyer decides whether the certificate meets its requirements.",
        ar: "لا. راجع المواصفة المطلوبة والأنشطة والمواقع التي تشملها الشهادة وصلاحيتها الحالية، وأي جهة منح أو هيئة اعتماد يشترطها المشتري بالاسم. ويعود للمشتري قرار استيفاء الشهادة لمتطلباته.",
      },
    },
    {
      q: {
        en: "How do I check if an ISO certificate is real?",
        ar: "كيف أتحقق من صحة شهادة أيزو؟",
      },
      a: {
        en: "Search IAF CertSearch by certificate number or company name and check status, scope and issuing body. A missing result alone does not prove non-accreditation; follow up with the issuer, accreditation body or IAF CertSearch.",
        ar: "ابحث في IAF CertSearch برقم الشهادة أو اسم الشركة، وراجع حالتها ونطاقها والجهة المانحة. عدم ظهور نتيجة لا يثبت وحده غياب الاعتماد؛ تواصل مع الجهة المانحة أو هيئة الاعتماد أو دعم IAF CertSearch لاستكمال التحقق.",
      },
    },
    {
      q: { en: "Is IraqCert accredited?", ar: "هل عراق سيرت معتمدة؟" },
      a: {
        en: "IraqCert provides preparation support and coordinates the process. Partner certification body AQSR holds UAF accreditation No. CM-MS-7820 within its disclosed scope. The certification audit and decision belong to the certification body, not IraqCert.",
        ar: "تقدم عراق سيرت دعم التحضير وتنسّق الإجراءات. وتحمل جهة منح الشهادات الشريكة AQSR اعتماد UAF رقم CM-MS-7820 ضمن نطاقها المعلن. وتتولى الجهة المانحة تدقيق منح الشهادة وقرار إصدارها، وليس عراق سيرت.",
      },
    },
  ],
};

const compare = [
  {
    point: { en: "Accreditation oversight", ar: "رقابة هيئة الاعتماد" },
    acc: { en: "Within the accredited scope", ar: "ضمن نطاق الاعتماد" },
    non: { en: "No accreditation oversight for this certificate", ar: "لا تشملها رقابة اعتماد" },
  },
  {
    point: { en: "IAF CertSearch verification", ar: "التحقق عبر IAF CertSearch" },
    acc: {
      en: "Check the record; follow up if missing",
      ar: "راجع السجل واستكمل التحقق إن لم يظهر",
    },
    non: { en: "Not an accredited certification record", ar: "ليست سجلاً لشهادة معتمدة" },
  },
  {
    point: { en: "Buyer and tender acceptance", ar: "القبول لدى المشترين وفي المناقصات" },
    acc: { en: "Depends on the stated requirements", ar: "بحسب المتطلبات المحددة" },
    non: {
      en: "Does not meet an accredited-certification requirement",
      ar: "لا تلبّي شرط الحصول على شهادة معتمدة",
    },
  },
];

export function AccreditedVsNonBody() {
  const { t } = useLanguage();
  return (
    <>
      <P>
        {t({
          en: "To respond to a customer’s request for accredited ISO certification, establish the requested standard, the proposed certification body and its relevant accreditation scope. Then confirm that the certificate will cover the activities and sites the customer needs.",
          ar: "لتلبية طلب عميل بالحصول على شهادة أيزو معتمدة، حدّد المواصفة المطلوبة وجهة منح الشهادة المقترحة ونطاق اعتمادها ذي الصلة. ثم تأكّد من أن الشهادة ستشمل الأنشطة والمواقع التي يحتاج العميل إلى شمولها.",
        })}
      </P>
      <P>
        {t({
          en: "IraqCert reviews the customer’s wording with you before defining preparation support and the proposed certification route. This guide explains the checks behind that discussion.",
          ar: "تراجع IraqCert معك نص متطلب العميل قبل تحديد دعم التحضير ومسار الحصول على الشهادة المقترح. يوضح هذا الدليل الجوانب التي نتحقق منها خلال هذه المناقشة.",
        })}
      </P>
      <P>
        <LangLink
          to="/insights/is-iso-certification-recognized-in-iraq"
          className="font-semibold text-primary hover:underline"
        >
          {t({
            en: "Check certificate acceptance for a buyer in Iraq",
            ar: "راجع قبول الشهادة لدى مشترٍ في العراق",
          })}
        </LangLink>
      </P>
      <H2 id="meaning">
        {t({
          en: "What accreditation tells you",
          ar: "ماذا يثبت الاعتماد؟",
        })}
      </H2>
      <P>
        {t({
          en: "The certification body assesses your management system and decides whether to issue the certificate. The accreditation body assesses the certification body’s competence for specified certification work. Accreditation for one standard does not automatically cover every certificate the body offers.",
          ar: "تقيّم جهة منح الشهادات نظام الإدارة لدى شركتك وتتخذ قرار منح الشهادة. أما جهة الاعتماد فتقيّم كفاءة الجهة المانحة لأعمال محددة في مجال منح الشهادات. واعتماد الجهة لمواصفة معينة لا يشمل تلقائياً جميع الشهادات التي تقدمها.",
        })}
      </P>
      <P>
        {t({
          en: "ISO develops standards and does not issue certificates. Its guidance explains that accreditation provides independent confirmation of competence; it also states that non-accreditation alone does not mean a certification body is not reputable.",
          ar: "تضع منظمة ISO المواصفات ولا تصدر الشهادات. وتوضح إرشاداتها أن الاعتماد يوفر تأكيداً مستقلاً للكفاءة، وأن غياب الاعتماد وحده لا يعني أن جهة منح الشهادات غير موثوقة.",
        })}
      </P>
      <P>
        <a
          href="https://www.iso.org/certification.html"
          target="_blank"
          rel="noopener noreferrer"
          className="font-semibold text-primary underline underline-offset-4"
        >
          {t({
            en: "ISO: certification and choosing a certification body",
            ar: "ISO: منح الشهادات واختيار الجهة المانحة",
          })}
        </a>
      </P>
      <H2 id="buyer">
        {t({
          en: "Start with the work the buyer wants covered",
          ar: "ابدأ بالأعمال التي يريد المشتري شمولها",
        })}
      </H2>
      <P>
        {t({
          en: "Consider an equipment supplier that also repairs equipment at customer sites. A certificate describing sales alone may leave the service work outside its scope. Accreditation does not resolve that mismatch. The standard, activities and locations still need to match what the customer has asked for.",
          ar: "لنفترض مورّداً يبيع المعدات ويصلحها أيضاً في مواقع العملاء. قد تترك شهادة يقتصر وصف نطاقها على المبيعات أعمال الخدمات خارج ذلك النطاق. ولا يعالج الاعتماد هذا الاختلاف؛ إذ ينبغي أن تتوافق المواصفة والأنشطة والمواقع مع ما طلبه العميل.",
        })}
      </P>
      <P>
        {t({
          en: "If the written requirement specifies accredited certification, a certificate issued outside the relevant accredited scope will not meet that condition. Ask the buyer to clarify any named bodies or other acceptance conditions before committing. Registration and contract decisions remain separate.",
          ar: "إذا اشترط المتطلب المكتوب شهادة صادرة ضمن نطاق اعتماد محدد، فلن تلبّي الشهادة الصادرة خارج النطاق المعني ذلك الشرط. اطلب من المشتري توضيح أي جهات محددة بالاسم أو شروط أخرى للقبول قبل الالتزام. وتبقى قرارات التسجيل وإحالة العقود مستقلة.",
        })}
      </P>
      <TableWrap>
        <thead className="bg-accent">
          <tr>
            <th scope="col" className={thClass}>
              {t({ en: "Point", ar: "المعيار" })}
            </th>
            <th scope="col" className={thClass}>
              {t({ en: "Accredited", ar: "معتمدة" })}
            </th>
            <th scope="col" className={thClass}>
              {t({ en: "Non-accredited", ar: "غير معتمدة" })}
            </th>
          </tr>
        </thead>
        <tbody>
          {compare.map((row) => (
            <tr key={row.point.en} className="border-t border-border">
              <td className={`${tdClass} font-semibold text-foreground`}>{t(row.point)}</td>
              <td className={tdClass}>{t(row.acc)}</td>
              <td className={tdClass}>{t(row.non)}</td>
            </tr>
          ))}
        </tbody>
      </TableWrap>

      <H2 id="verify">
        {t({
          en: "Verify the details, then follow up on gaps",
          ar: "تحقّق من التفاصيل وتابع ما يحتاج إلى توضيح",
        })}
      </H2>
      <P>
        {t({
          en: "Search IAF CertSearch using the certificate number or company name. Compare the record’s standard, certified scope and locations, status, certification body and accreditation body with the certificate. A missing result may mean the record has not been added, is confidential or has been removed. Ask the issuer or accreditation body for confirmation, or submit an IAF CertSearch inquiry.",
          ar: "ابحث في IAF CertSearch برقم الشهادة أو اسم الشركة. وطابق المواصفة ونطاق الشهادة والمواقع المشمولة وحالتها والجهة المانحة وهيئة الاعتماد مع بيانات الشهادة. قد لا تظهر نتيجة لأن السجل لم يُضف بعد، أو لأن بياناته سرية، أو لأنه أُزيل من القاعدة. اطلب التأكيد من الجهة المانحة أو هيئة الاعتماد، أو أرسل استفساراً إلى IAF CertSearch.",
        })}
      </P>

      <P>
        <a
          href="https://www.iafcertsearch.org/"
          target="_blank"
          rel="noopener noreferrer"
          className="font-semibold text-primary underline underline-offset-4"
        >
          {t({ en: "Search the IAF CertSearch database", ar: "ابحث في قاعدة IAF CertSearch" })}
        </a>
      </P>
      <H2 id="iraqcert-role">
        {t({
          en: "IraqCert’s role and the certification body",
          ar: "دور عراق سيرت وجهة منح الشهادة",
        })}
      </H2>
      <P>
        {t({
          en: "IraqCert prepares clients and coordinates the process; the independent certification body conducts the certification audit and decides on issuance. Partner body AQSR’s disclosed UAF scope under No. CM-MS-7820 covers ISO 9001, ISO 14001, ISO 45001, ISO 27001 and ISO 22000. ISO 29001 is outside that scope, so IraqCert confirms the issuing body and its accreditation scope before booking an ISO 29001 audit. See ",
          ar: "تساعد عراق سيرت العملاء على التحضير وتنسّق الإجراءات؛ وتتولى جهة منح الشهادات المستقلة تدقيق منح الشهادة وقرار إصدارها. ويشمل نطاق اعتماد UAF المعلن للجهة الشريكة AQSR، رقم CM-MS-7820، مواصفات ISO 9001 وISO 14001 وISO 45001 وISO 27001 وISO 22000. أما ISO 29001 فهو خارج هذا النطاق، لذا تؤكد عراق سيرت الجهة المانحة ونطاق اعتمادها قبل حجز تدقيق ISO 29001. اطّلع على ",
        })}
        <LangLink to="/services" className="font-semibold text-primary hover:underline">
          {t({ en: "our ISO certification services", ar: "خدمات شهادات الأيزو لدينا" })}
        </LangLink>
        {t({ en: ".", ar: "." })}
      </P>
      <P>
        {t({
          en: "Send IraqCert the customer’s wording, the requested standard and the activities and sites to be covered. Those details let us discuss preparation and the proposed issuing route on a clear basis.",
          ar: "أرسل إلى IraqCert نص متطلب العميل والمواصفة المطلوبة والأنشطة والمواقع المراد شمولها. تتيح هذه التفاصيل مناقشة التحضير ومسار إصدار الشهادة المقترح على أساس واضح.",
        })}
      </P>
    </>
  );
}
